import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { t } from "i18next";

import ButtonSpinner from "components/ButtonSpinner";
import { CartAction } from "store/actions";
import { useCustomHistory } from "hooks";
import { useMapContext } from "contexts/googleMap";
import useCheckStoreAvailability from "service-hooks/useCheckStoreAvailability";
import { calculateCartGrossAmount } from "utils/Utils";
import C from "Constants";
import { CustomButton } from "components/Button";
import { TranslationText } from "components/TextTranslationContainer";
import { PriceRender } from "components/DisplayPrice";

function PlaceOrderButton({ placeOrder, loading, text = "Place Order", disabled=false }) {
  const dispatch = useDispatch();
  const { reset } = useMapContext();
  const { checkStoreAvailability } = useCheckStoreAvailability();

  //global state
  const { cart } = useSelector((state) => state?.cart);
  const { ordered } = useSelector((state) => state?.order);
  const { allOrders } = ordered;
  const { discountInfo } = useSelector((state) => state?.Discounts);
  const { selectedStore } = useSelector((state) => state?.store);
  const { orderTiming } = useSelector((state) => state?.orderTimingReducer);
  const { brandInfo } = useSelector((state) => state?.brandStorageReducer);
  const { orderType } = useSelector((state) => state?.AppReducer);
  const { selectedPaymentType } = useSelector(
    (state) => state.PaymentMethodReducer
  );

  useEffect(() => {
    const {
      itemsGrossAmount,
      itemsNetPrice,
      orderGrossAmount,
      totalOrdersGrossAmount,
    } = calculateCartGrossAmount(cart, discountInfo, 0, ordered);
    dispatch(
      CartAction.updateCartPrice({
        originalPrice: itemsNetPrice,
        amountAfterDiscount: itemsGrossAmount,
        itemsGrossAmount,
        orderGrossAmount,
        totalOrdersGrossAmount,
      })
    );
  }, [cart, discountInfo, dispatch, selectedStore]);

  const placeMyOrder = async () => {
    if (orderTiming?.TimingType === C.ORDER_TIMING_TYPE.PREORDER) {
      placeOrder();
      reset();
    } else {
      const orderDetails = {
        brandId: brandInfo?.brand?._id,
        storeId: selectedStore?._id,
        serviceType: orderType?.serviceType,
      };
      const isOpenStore = await checkStoreAvailability(orderDetails);
      if (!isOpenStore) return;
      placeOrder();
      reset();
    }
  };

  const ButtonContent = (loading) => {
    return (
      <>
        {loading && (
          <ButtonSpinner
            height="30px"
            width="30px"
            color="text-white mx-1 inline-block"
          />
        )}
        <span className="font-bold text-17x">
          {loading ? t("Payment processing…") : t(text)}
        </span>
      </>
    );
  };

  return (
    <React.Fragment>
      {((allOrders?.length > 0 && selectedPaymentType !== C.PAY_LATER) ||
        cart?.length > 0) && (
        <CustomButton onClick={placeMyOrder} disabled={loading || disabled} type="submit">
          {ButtonContent(loading)}
        </CustomButton>
      )}
    </React.Fragment>
  );
}

export default PlaceOrderButton;

function MinOrder({ minOrder, cartTotal, storePageUrl, disabled=false }) {
  const router = useCustomHistory();
  const params = useParams();
  const { DomainType } = useSelector((state) => state.AppReducer);
  const getValue = () => {
    const orderValue = minOrder - cartTotal;
    return orderValue >= 0 ? orderValue?.toFixed(2) : 0;
  };
  const gotoStorePage = () =>
    router(storePageUrl, `/${params?.domainName}${storePageUrl}`, DomainType);

  return (
    <CustomButton
      className="w-full flex gap-1 flex-wrap items-center justify-center"
      onClick={gotoStorePage}
      disabled={disabled}
    >
      <TranslationText text={"Add"} className="text-white" />
      <PriceRender
        price={+getValue()}
        className="text-white"
        currencySymbolClassName="text-white"
      />
      <TranslationText text="more" className="text-white" />
    </CustomButton>
  );
}
export { MinOrder };
